<template>
    <div class="dba">
        <div class="dba-acc" v-for="(section, i) in localSections" :key="section.key || i"
            :class="{ 'dba-acc--copying': copyingSection === i, 'dba-acc--new': newSection === i }">
            <div class="dba-hd" :class="{ on: isOpen(i) }" :style="headStyle(i)" @click="toggle(i)">
                <span class="dba-title">{{ section.title }}</span>
                <span class="dba-count">{{ sectionCount(section) }}</span>
                <button-tooltip v-if="duplicable" top :tooltip="duplicateSectionTooltip">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn v-on="on" v-bind="attrs" icon x-small class="dba-dupe" :style="{ color: color(i) }"
                            @click.stop="onDuplicateSection(section, i)">
                            <v-icon size="15">mdi-content-copy</v-icon>
                        </v-btn>
                    </template>
                </button-tooltip>
                <button-tooltip v-if="sectionDeletable" top :tooltip="deleteSectionTooltip"
                    :disabled="!deleteSectionTooltip">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn v-on="on" v-bind="attrs" icon x-small class="dba-del-section"
                            @click.stop="$emit('delete-section', section, i)">
                            <v-icon size="15">mdi-trash-can-outline</v-icon>
                        </v-btn>
                    </template>
                </button-tooltip>
                <v-icon class="dba-chev" size="16" :style="{ color: color(i) }">mdi-chevron-down</v-icon>
            </div>
            <v-expand-transition>
                <div class="dba-body" v-show="isOpen(i)">
                    <div class="dba-empty" v-if="!section.items || !section.items.length">{{ emptyText }}</div>
                    <draggable v-model="section.items" :disabled="!sortable" :group="{ name: 'dba-trainings' }"
                        handle=".dba-drag" ghost-class="dba-ghost" @start="dragging = true"
                        @end="onDragEnd">
                        <div class="dba-row" v-for="(item, j) in section.items" :key="item.key || j"
                            :class="{ 'dba-row--click': item.clickable || duplicable,
                                'dba-row--copying': isCopying(i, j), 'dba-row--new': isNewCopy(i, j) }"
                            @click="onItemClick(item, i, j)"
                            @dblclick="onItemDblClick(item, i, j)"
                            @mouseenter="onRowEnter($event, item)" @mouseleave="onRowLeave">
                            <span v-if="sortable" class="dba-drag" @click.stop>
                                <v-icon size="16">mdi-drag-vertical</v-icon>
                            </span>
                            <div class="dba-thumb" :class="{ 'dba-thumb--play': item.video }">
                                <v-img :src="item.thumb" height="48" width="64" />
                                <div class="dba-play" v-if="item.video">
                                    <v-icon size="15" color="#fff">mdi-play</v-icon>
                                </div>
                            </div>
                            <div class="dba-info">
                                <div class="dba-name">{{ item.name }}</div>
                                <div class="dba-meta">{{ item.meta }}</div>
                            </div>
                            <button-tooltip v-if="duplicable" top :tooltip="duplicateItemTooltip">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn v-on="on" v-bind="attrs" icon small class="dba-dupe-item"
                                        @click.stop="onDuplicateItem(item, i, j)">
                                        <v-icon size="16">mdi-content-copy</v-icon>
                                    </v-btn>
                                </template>
                            </button-tooltip>
                            <button-tooltip v-if="deletable" top :tooltip="deleteItemTooltip"
                                :disabled="!deleteItemTooltip">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn v-on="on" v-bind="attrs" icon small class="dba-del"
                                        @click.stop="onDelete(item, i, j)">
                                        <v-icon size="18">mdi-trash-can-outline</v-icon>
                                    </v-btn>
                                </template>
                            </button-tooltip>
                        </div>
                    </draggable>
                </div>
            </v-expand-transition>
        </div>

        <div class="dba-popup" v-if="hoverPopup && !dragging" :style="popupStyle">
            <slot name="popup" :items="hoverPopup">
                <div class="dba-popup-item" v-for="(p, k) in hoverPopup" :key="k">
                    <span class="dba-popup-dot" :style="{ background: p.color }" />
                    <span class="dba-popup-name">{{ p.name }}</span>
                    <span class="dba-popup-presc">{{ p.meta }}</span>
                </div>
            </slot>
        </div>
    </div>
</template>

<script>

import draggable from "vuedraggable";
import ButtonTooltip from "@/components/common/ButtonTooltip";

const DOUBLE_CLICK_DELAY = 250;
const COPY_FEEDBACK_TIMEOUT = 4000;
const NEW_COPY_HIGHLIGHT = 1400;

const BLOCK_HUES = [
    { bg: 'rgba(51,153,153,.22)', color: '#339999' },
    { bg: 'rgba(44,160,140,.2)', color: '#2ca08c' },
    { bg: 'rgba(56,168,100,.2)', color: '#38a864' },
    { bg: 'rgba(80,176,60,.2)', color: '#50b03c' },
    { bg: 'rgba(110,185,20,.2)', color: '#6eb914' },
    { bg: 'rgba(148,192,5,.2)', color: '#94c005' },
    { bg: 'rgba(180,208,2,.2)', color: '#b4d002' },
    { bg: 'rgba(204,230,10,.2)', color: '#aacc00' },
]

export default {
    name: 'DetailBlocksAccordion',
    components: { draggable, ButtonTooltip },
    props: {
        sections: {
            type: Array,
            default: () => [],
        },
        emptyText: {
            type: String,
            default: '',
        },
        sortable: {
            type: Boolean,
            default: false,
        },
        deletable: {
            type: Boolean,
            default: false,
        },
        duplicable: {
            type: Boolean,
            default: false,
        },
        duplicateSectionTooltip: {
            type: String,
            default: '',
        },
        duplicateItemTooltip: {
            type: String,
            default: '',
        },
        sectionDeletable: {
            type: Boolean,
            default: false,
        },
        deleteSectionTooltip: {
            type: String,
            default: '',
        },
        deleteItemTooltip: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            openBlocks: [0],
            hoverPopup: null,
            popupStyle: {},
            localSections: [],
            dragging: false,
            clickTimeout: null,
            copyingPos: null,
            newCopyPos: null,
            copyingSection: null,
            newSection: null,
        }
    },
    watch: {
        sections: {
            immediate: true,
            handler(val) {
                this.localSections = (val || []).map(s => ({ ...s, items: [...(s.items || [])] }))
                this.resolvePendingCopy()
                this.resolvePendingSection()
            },
        },
    },
    beforeDestroy() {
        clearTimeout(this.clickTimeout)
        clearTimeout(this._copyTimeout)
        clearTimeout(this._newCopyTimeout)
        clearTimeout(this._sectionTimeout)
        clearTimeout(this._newSectionTimeout)
    },
    methods: {
        isOpen(index) {
            return this.openBlocks.includes(index)
        },
        onRowEnter(e, item) {
            if (this.dragging || !item.popup || !item.popup.length) return
            const rect = e.currentTarget.getBoundingClientRect()
            const width = 260
            let left = rect.left - width - 10
            if (left < 8) left = rect.right + 10
            const maxH = window.innerHeight * 0.55
            let top = rect.top
            if (top + maxH > window.innerHeight - 8) top = Math.max(8, window.innerHeight - maxH - 8)
            this.hoverPopup = item.popup
            this.popupStyle = { left: `${left}px`, top: `${top}px` }
        },
        onRowLeave() {
            this.hoverPopup = null
        },
        toggle(index) {
            const vm = this
            if (vm.openBlocks.includes(index)) vm.openBlocks = vm.openBlocks.filter(i => i !== index)
            else vm.openBlocks = [...vm.openBlocks, index]
        },
        color(index) {
            return BLOCK_HUES[index % BLOCK_HUES.length].color
        },
        headStyle(index) {
            const hue = BLOCK_HUES[index % BLOCK_HUES.length]
            return { background: hue.bg, color: hue.color }
        },
        sectionCount(section) {
            if (this.sortable) return section.items ? section.items.length : 0
            if (section.count != null) return section.count
            return section.items ? section.items.length : 0
        },
        onItemClick(item, sectionIndex, itemIndex) {
            const vm = this
            if (!item.clickable) return
            if (!vm.duplicable) return vm.$emit('item-click', item, sectionIndex, itemIndex)
            clearTimeout(vm.clickTimeout)
            vm.clickTimeout = setTimeout(() => {
                vm.$emit('item-click', item, sectionIndex, itemIndex)
            }, DOUBLE_CLICK_DELAY)
        },
        onItemDblClick(item, sectionIndex, itemIndex) {
            const vm = this
            if (!vm.duplicable) return
            clearTimeout(vm.clickTimeout)
            vm.onDuplicateItem(item, sectionIndex, itemIndex)
        },
        onDuplicateSection(section, sectionIndex) {
            const vm = this
            vm.copyingSection = sectionIndex
            vm._pendingSection = { index: sectionIndex, count: vm.localSections.length }
            clearTimeout(vm._sectionTimeout)
            vm._sectionTimeout = setTimeout(() => {
                vm.copyingSection = null
                vm._pendingSection = null
            }, COPY_FEEDBACK_TIMEOUT)
            vm.$emit('duplicate-section', section, sectionIndex)
        },
        resolvePendingSection() {
            const vm = this
            const pending = vm._pendingSection
            if (!pending) return
            const grew = vm.localSections.length - pending.count
            if (grew <= 0) return
            vm._pendingSection = null
            vm.copyingSection = null
            clearTimeout(vm._sectionTimeout)
            if (grew !== 1) return
            vm.newSection = pending.index + 1
            clearTimeout(vm._newSectionTimeout)
            vm._newSectionTimeout = setTimeout(() => { vm.newSection = null }, NEW_COPY_HIGHLIGHT)
        },
        onDuplicateItem(item, sectionIndex, itemIndex) {
            const vm = this
            vm.markCopying(sectionIndex, itemIndex)
            vm.$emit('duplicate-item', item, sectionIndex, itemIndex)
        },
        isCopying(sectionIndex, itemIndex) {
            const pos = this.copyingPos
            return !!pos && pos.section === sectionIndex && pos.item === itemIndex
        },
        isNewCopy(sectionIndex, itemIndex) {
            const pos = this.newCopyPos
            return !!pos && pos.section === sectionIndex && pos.item === itemIndex
        },
        markCopying(sectionIndex, itemIndex) {
            const vm = this
            const items = vm.localSections[sectionIndex]?.items || []
            vm.copyingPos = { section: sectionIndex, item: itemIndex }
            vm._pendingCopy = { section: sectionIndex, item: itemIndex, count: items.length }
            clearTimeout(vm._copyTimeout)
            vm._copyTimeout = setTimeout(() => {
                vm.copyingPos = null
                vm._pendingCopy = null
            }, COPY_FEEDBACK_TIMEOUT)
        },
        resolvePendingCopy() {
            const vm = this
            const pending = vm._pendingCopy
            if (!pending) return
            const items = vm.localSections[pending.section]?.items || []
            if (items.length <= pending.count) return
            vm._pendingCopy = null
            vm.copyingPos = null
            clearTimeout(vm._copyTimeout)
            vm.newCopyPos = { section: pending.section, item: pending.item + 1 }
            clearTimeout(vm._newCopyTimeout)
            vm._newCopyTimeout = setTimeout(() => { vm.newCopyPos = null }, NEW_COPY_HIGHLIGHT)
        },
        onDragEnd() {
            const vm = this
            vm.dragging = false
            const flat = []
            vm.localSections.forEach(s => (s.items || []).forEach(it => flat.push(it)))
            vm.$emit('reorder', flat)
        },
        onDelete(item, sectionIndex, itemIndex) {
            this.$emit('delete-item', item, sectionIndex, itemIndex)
        },
    },
}
</script>

<style scoped>
.dba-acc {
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    margin-bottom: 8px;
    overflow: hidden;
}

.dba-hd {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    cursor: pointer;
    user-select: none;
    transition: filter .15s;
}

.dba-hd:hover {
    filter: brightness(1.15);
}

.dba-title {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: .05em;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.dba-count {
    min-width: 26px;
    height: 26px;
    border-radius: 6px;
    border: 1.5px solid rgba(0, 0, 0, .12);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    color: inherit;
    background: rgba(255, 255, 255, .18);
}

.dba-chev {
    transition: transform .2s;
    flex-shrink: 0;
}

.dba-hd.on .dba-chev {
    transform: rotate(180deg);
}

.dba-body {
    padding: 4px 14px 8px;
    background: var(--v-bg-base);
}

.dba-empty {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    text-align: center;
    padding: 10px 0;
}

.dba-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid var(--v-outline-base);
}

.dba-row:last-child {
    border-bottom: none;
}

.dba-row--click {
    cursor: pointer;
}

.dba-acc--copying {
    opacity: .65;
}

.dba-acc--new {
    animation: dba-acc-in 1.4s ease-out;
}

@keyframes dba-acc-in {
    0% {
        box-shadow: 0 0 0 2px var(--v-accent-base);
    }

    100% {
        box-shadow: 0 0 0 2px rgba(51, 153, 153, 0);
    }
}

.dba-row--copying {
    background: rgba(51, 153, 153, 0.1);
    opacity: .65;
}

.dba-row--new {
    animation: dba-copy-in 1.4s ease-out;
}

@keyframes dba-copy-in {
    0% {
        background: rgba(51, 153, 153, 0.28);
    }

    100% {
        background: transparent;
    }
}

.dba-drag {
    display: flex;
    cursor: grab;
    color: var(--v-txtMuted-base);
    flex-shrink: 0;
}

.dba-drag .v-icon {
    color: inherit !important;
}

.dba-ghost {
    opacity: .5;
}

.dba-thumb {
    width: 64px;
    height: 48px;
    border-radius: 6px;
    overflow: hidden;
    background: var(--v-surface2-base);
    flex-shrink: 0;
    position: relative;
}

.dba-play {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, .25);
    transition: background .15s;
}

.dba-thumb--play:hover .dba-play {
    background: rgba(0, 0, 0, .4);
}

.dba-info {
    min-width: 0;
    flex: 1;
}

.dba-dupe {
    flex-shrink: 0;
    opacity: 0.75;
}

.dba-dupe:hover {
    opacity: 1;
}

.dba-del-section {
    flex-shrink: 0;
    color: inherit !important;
    opacity: .75;
}

.dba-del-section:hover {
    color: var(--v-error-base) !important;
    opacity: 1;
}

.dba-dupe-item {
    flex-shrink: 0;
    color: var(--v-txtMuted-base) !important;
}

.dba-dupe-item:hover {
    color: var(--v-accent-base) !important;
}

.dba-del {
    flex-shrink: 0;
    color: var(--v-txtMuted-base) !important;
}

.dba-del:hover {
    color: var(--v-error-base) !important;
}

.dba-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.dba-meta {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    margin-top: 2px;
}

.dba-popup {
    position: fixed;
    width: 260px;
    max-height: 55vh;
    overflow-y: auto;
    overflow-x: hidden;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    box-shadow: 0 8px 28px rgba(0, 0, 0, .4);
    padding: 6px 0;
    z-index: 600;
    pointer-events: none;
}

.dba-popup-item {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 7px 14px;
}

.dba-popup-dot {
    width: 9px;
    height: 9px;
    border-radius: 3px;
    flex-shrink: 0;
}

.dba-popup-name {
    font-size: 12px;
    font-weight: 500;
    color: var(--v-txtPrimary-base);
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.dba-popup-presc {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    flex-shrink: 0;
}
</style>
